import Card, { CardHeader, CardBody } from '../../components/ui/Card';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { anfragenApi } from '../../api/anfragen';
import { adressenApi } from '../../api/adressen';
import { vermieterApi } from '../../api/vermieter';
import { verfahrenApi } from '../../api/verfahren';
import type { Anfrage, StateInfo } from '../../types/index';
import { useReturnTo } from '../../hooks/usePageNavigation';
import VerfahrenPicker from '../../components/proceedings/VerfahrenPicker';
import AnfragenStateBadge from '../../components/requests/AnfragenStateBadge';
import StateChangeWidget from '../../components/status/StateChangeWidget';
import AssignmentWidget from '../../components/assignments/AssignmentWidget';
import AufgabenBlock from '../../components/tasks/AufgabenBlock';
import VermieterPicker from '../../components/landlords/VermieterPicker';
import AddressHierarchySection from '../../components/addresses/AddressHierarchySection';
import BebauungsplanInfoCard from '../../components/maps/BebauungsplanInfoCard';
import EditableTextSection from '../../components/ui/EditableTextSection';
import AuditTimeline from '../../components/audit/AuditTimeline';
import DocumentList from '../../components/documents/DocumentList';
import KommunikationSection from '../../components/communication/KommunikationSection';
import EntityLinkButton from '../../components/ui/EntityLinkButton';
import MapLinkButton from '../../components/maps/MapLinkButton';
import ConfirmDeleteModal from '../../components/ui/ConfirmDeleteModal';
import { restoreOne } from '../../api/softDelete';
import { useCan } from '../../hooks/useCan';
import { useDocumentConfig } from '../../hooks/useConfigVisibility';

const AnfrageDetail: React.FC = () => {
  const { anfrageId } = useParams<{ anfrageId: string }>();
  const navigate = useNavigate();
  const returnTo = useReturnTo('/anfragen');
  const queryClient = useQueryClient();
  const docTab = useDocumentConfig('anfrage').tab;
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'dokumente' | 'verlauf'>('uebersicht');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const canRestore = useCan('restore_own');
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('anfrage', Number(anfrageId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
    },
  });

  const { data: anfrage, isLoading, error } = useQuery({
    queryKey: ['anfrage', anfrageId],
    queryFn: () => anfragenApi.getAnfrage(Number(anfrageId)),
    enabled: !!anfrageId,
  });

  const { data: vermieterDetail } = useQuery({
    queryKey: ['vermieter', anfrage?.vermieter_id],
    queryFn: () => vermieterApi.getVermieterDetail(anfrage!.vermieter_id!),
    enabled: !!anfrage?.vermieter_id,
  });

  // Koordinaten für den Bebauungsplan-Abgleich liegen auf der verknüpften Adresse.
  const { data: adresseForBplan } = useQuery({
    queryKey: ['adresse', anfrage?.adresse_id],
    queryFn: () => adressenApi.getAdresse(anfrage!.adresse_id!),
    enabled: !!anfrage?.adresse_id,
  });

  const { data: history } = useQuery({
    queryKey: ['anfrage', anfrageId, 'verlauf'],
    queryFn: () => anfragenApi.getHistory(Number(anfrageId)),
    enabled: !!anfrageId && activeTab === 'verlauf',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !anfrage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Anfrage konnte nicht geladen werden.</p>
          <button onClick={() => navigate(returnTo)} className="btn btn-text mt-4">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate(returnTo)} className="btn btn-text mb-2 text-sm">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {anfrage.adresse_text || 'Anfrage'}
                {anfrage.zipcode && <span className="text-gray-500 font-normal text-xl ml-2">{anfrage.zipcode}</span>}
                {anfrage.city && <span className="text-gray-500 font-normal text-xl ml-1">{anfrage.city}</span>}
              </h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <AnfragenStateBadge status={anfrage.status} />
                {anfrage.adresse_id ? (
                  <EntityLinkButton
                    label={`${anfrage.adresse_text}${anfrage.zipcode ? `, ${anfrage.zipcode}` : ''}`}
                    onClick={() => navigate(`/adressen/${anfrage.adresse_id}`)}
                    color="green"
                    requireModule="adressen"
                  />
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-amber-100 text-amber-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Keine Adresse zugeordnet
                  </span>
                )}
                {anfrage.verfahren_id && (
                  <EntityLinkButton
                    label={`Verfahren #${anfrage.verfahren_id}`}
                    onClick={() => navigate(`/verfahren/${anfrage.verfahren_id}`)}
                    color="purple"
                    requireModule="verfahren"
                  />
                )}
                {anfrage.vermieter_id && (
                  <EntityLinkButton
                    label={vermieterDetail?.name || `Vermieter #${anfrage.vermieter_id}`}
                    onClick={() => navigate(`/vermieter/${anfrage.vermieter_id}`)}
                    color="teal"
                    requireModule="vermieter"
                  />
                )}
                {anfrage.adresse_id != null && (
                  <MapLinkButton to={`/karte?anfrage=${anfrage.id}`} />
                )}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 flex flex-col gap-2 items-end">
              <AnfrageStateChanger anfrage={anfrage} />
              {anfrage.deleted_at ? (
                canRestore && (
                  <button
                    onClick={() => restoreMutation.mutate()}
                    disabled={restoreMutation.isPending}
                    className="btn btn-secondary bg-success-50 text-success-700 border-success-200 enabled:hover:bg-success-100 focus-visible:ring-success-500 px-3 py-1.5 text-xs border"
                  >
                    Wiederherstellen
                  </button>
                )
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn-secondary bg-danger-50 text-danger-700 border-danger-200 enabled:hover:bg-danger-100 focus-visible:ring-danger-500 px-3 py-1.5 text-xs border"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="anfrage"
        ids={[Number(anfrageId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['anfragen'] });
          navigate(returnTo);
        }}
      />

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'uebersicht', label: 'Übersicht' },
              ...(docTab ? [{ key: 'dokumente', label: 'Dokumente' }] : []),
              { key: 'verlauf', label: 'Verlauf' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {activeTab === 'uebersicht' && (
          <div className="space-y-6">
            <AssignmentWidget
              entityType="anfrage"
              entityId={anfrage.id}
              assignment={anfrage.assignment ?? null}
              invalidateKeys={[['anfrage', anfrageId!], ['anfragen']]}
            />
            <AufgabenBlock
              entityType="anfrage"
              entityId={anfrage.id}
              invalidateKeys={[['anfrage', anfrageId!]]}
            />
            <AddressHierarchySection
              entityId={anfrage.id}
              isAssigned={anfrage.address_verified}
              initialAdresseId={anfrage.adresse_id}
              initialHausId={anfrage.haus_id}
              initialEinheitId={anfrage.einheit_id}
              prefillAddress={anfrage.adresse_text}
              prefillZipcode={anfrage.zipcode}
              prefillCity={anfrage.city}
              onSave={async (result) => {
                await anfragenApi.updateAnfrage(anfrage.id, {
                  adresse_text: result.address,
                  zipcode: result.zipcode ?? undefined,
                  city: result.city ?? undefined,
                  address_verified: true,
                  adresse_id: result.adresseId,
                  haus_id: result.hausId,
                  einheit_id: result.einheitId,
                });
                queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
              }}
              onHierarchyChange={(data) => {
                anfragenApi.updateAnfrage(anfrage.id, data).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
                });
              }}
            />
            <BebauungsplanInfoCard
              latitude={adresseForBplan?.latitude ?? null}
              longitude={adresseForBplan?.longitude ?? null}
            />
            <VermieterPicker
              current={vermieterDetail ?? null}
              onAssign={async (v) => {
                await anfragenApi.updateAnfrage(anfrage.id, { vermieter_id: v.id });
                queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
              }}
              onUnlink={async () => {
                await anfragenApi.updateAnfrage(anfrage.id, { vermieter_id: null });
                queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
              }}
            />
            <EditableTextSection
              title="Notizen"
              value={anfrage.notes || ''}
              placeholder="Keine Notizen"
              multiline
              onSave={async (notes) => {
                await anfragenApi.updateAnfrage(anfrage.id, { notes });
                queryClient.invalidateQueries({ queryKey: ['anfrage', anfrageId] });
              }}
            />
            <AnfrageVerfahrenSection anfrage={anfrage} />
            {/* Erfassungsdetails */}
            <Card>
              <CardHeader title="Erfassungsdetails" />
              <CardBody padding="horizontal">
                <dl className="divide-y divide-gray-200">
                  <InfoRow label="Erstellt" value={new Date(anfrage.created_at).toLocaleString('de-DE')} />
                  <InfoRow label="Letzte Aktualisierung" value={anfrage.updated_at ? new Date(anfrage.updated_at).toLocaleString('de-DE') : 'Nie'} />
                </dl>
              </CardBody>
            </Card>
          </div>
        )}

        {activeTab === 'verlauf' && (
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Änderungsverlauf</h3>
            <AuditTimeline entries={history || []} />
          </Card>
        )}

        {activeTab === 'dokumente' && (
          <div className="space-y-6">
            <DocumentList entityType="anfrage" entityId={anfrage.id} />
            <KommunikationSection
              entityType="anfrage"
              entityId={anfrage.id}
              vermieterId={anfrage.vermieter_id ?? null}
            />
          </div>
        )}
      </main>
    </div>
  );
};

function AnfrageStateChanger({ anfrage }: { anfrage: Anfrage }) {
  const queryClient = useQueryClient();

  const { data: states = [] } = useQuery<StateInfo[]>({
    queryKey: ['anfrage-states'],
    queryFn: () => anfragenApi.getStates(),
  });

  const changeMutation = useMutation({
    mutationFn: ({ state, comment, handoff }: {
      state: string;
      comment: string;
      handoff?: { assignee_user_id: number; assignment_context: string };
    }) =>
      anfragenApi.changeState(anfrage.id, state, comment || undefined, handoff),
    onSuccess: (updated) => {
      queryClient.setQueryData(['anfrage', String(anfrage.id)], updated);
      queryClient.invalidateQueries({ queryKey: ['aufgaben'] });
    },
  });

  return (
    <StateChangeWidget
      currentState={anfrage.status}
      options={states}
      onChangeState={(state, comment, handoff) =>
        changeMutation.mutateAsync({ state, comment, handoff }).then(() => {})
      }
      isPending={changeMutation.isPending}
      enableHandoff
      currentAssigneeUserId={anfrage.zustaendig_user_id}
    />
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value}</dd>
    </div>
  );
}

function AnfrageVerfahrenSection({ anfrage }: { anfrage: Anfrage }) {
  const queryClient = useQueryClient();

  const { data: verfahren } = useQuery({
    queryKey: ['verfahren', anfrage.verfahren_id],
    queryFn: () => verfahrenApi.getVerfahrenDetail(anfrage.verfahren_id!),
    enabled: !!anfrage.verfahren_id,
  });

  return (
    <VerfahrenPicker
      current={anfrage.verfahren_id ? { id: anfrage.verfahren_id, aktenzeichen: verfahren?.aktenzeichen ?? null } : null}
      onLinkExisting={async (verfahrenId) => {
        await anfragenApi.updateAnfrage(anfrage.id, { verfahren_id: verfahrenId });
        queryClient.invalidateQueries({ queryKey: ['anfrage', String(anfrage.id)] });
      }}
      onUnlink={async () => {
        await anfragenApi.updateAnfrage(anfrage.id, { verfahren_id: null });
        queryClient.invalidateQueries({ queryKey: ['anfrage', String(anfrage.id)] });
      }}
      onCreateAndLink={async (data) => {
        await anfragenApi.createVerfahren(anfrage.id, data);
        queryClient.invalidateQueries({ queryKey: ['anfrage', String(anfrage.id)] });
      }}
      unlinkedHint="Dieser Anfrage ist noch kein Verfahren zugeordnet."
    />
  );
}

export default AnfrageDetail;
